import {
  useAchievementInfo,
  useAppContext,
  useConfirmOrUnlinkFacebookFundraiser,
  useGifts,
} from '@frx/frx-react-context';
import { sortBy } from 'lodash/fp';
import { useNavigate } from 'react-router-dom';
import {
  FirstDonationLocked,
  FirstDonationUnlocked,
  GoalMetLocked,
  GoalMetUnLocked,
  High5Locked,
  High5Unlocked,
  KickstartLocked,
  KickstartUnLocked,
  LinkToFacebookLocked,
  LinkToFacebookUnLocked,
  PicturePerfectLocked,
  PicturePerfectUnLocked,
  SharingIsCaringLocked,
  SharingIsCaringUnlocked,
  StoryTellerLocked,
  StoryTellerUnLocked,
  SuperThankfulLocked,
  SuperThankfulUnlocked,
  ThankfulLocked,
  ThankfulUnlocked,
} from '../assets';
import { getUniqueObjectsInArrayByKey } from '../utils';
import { useEventId } from './use-eventId';
import { useFbFundraiser } from './use-fb-fundraiser';
import { useSocial } from './interactions';

export const useAchievements = () => {
  const navigateTo = useNavigate();
  const frId = useEventId();
  const {
    data: {
      authData: { user },
      eventData: { eventInfo },
    },
  } = useAppContext();
  const { sharingIsCaringInteraction } = useSocial();
  const { data: giftsList } = useGifts({
    cons_id: user?.cons_id as number,
    fr_id: eventInfo?.id as number,
    list_page_size: 50,
    list_ascending: false,
  });
  const gifts = giftsList?.gift;

  let uniqueGifts = []; // unique gifts by email + recognition name, not including the current user
  let uniqueThankedGifts = []; // only check unique acknowledged donations, not including the user

  if (gifts) {
    const cashGifts = getUniqueObjectsInArrayByKey(
      gifts.filter((gift) => gift.email === null) as [],
      ['recognitionName']
    );
    const checkGifts = getUniqueObjectsInArrayByKey(
      gifts.filter((gift) => gift.email === 'noemail@noemail.com') as [],
      ['recognitionName']
    );

    uniqueGifts = getUniqueObjectsInArrayByKey(gifts as [], ['email']).filter(
      (gift) =>
        gift.email &&
        gift.email !== 'noemail@noemail.com' &&
        gift.email !== user?.email?.primary_address
    );

    cashGifts.forEach((gift) => uniqueGifts.push(gift));
    checkGifts.forEach((gift) => uniqueGifts.push(gift));

    uniqueThankedGifts = uniqueGifts.filter((gift) => gift.acknowledged);
  }

  const { data: facebookFundraiserStatus } =
    useConfirmOrUnlinkFacebookFundraiser();

  const { data: achievements, isInitialLoading }: any = useAchievementInfo({
    facebookFundraiserId: `[[S48:${frId}-${user?.cons_id}:facebook-fundraiser-id]]`,
    goalMet: `[[?[[S48:${frId}-${user?.cons_id}:if-percent-gt:99]]::TRUE::true::false]]`,
    kickstartDonor: `[[?[[S48:${frId}-${user?.cons_id}:if-is-self-donor]]::TRUE::true::false]]`,
    storyTeller: `[[?[[S48:${frId}-${user?.cons_id}:if-page-updated]]::TRUE::true::false]]`,
  });

  const { handleFbLogin, linkFbIsLoading } = useFbFundraiser();
  const photoExists = user?.avatar?.includes(`${frId}.`);

  const CREATE_CLIENT_ACHIEVEMENTS: any[] = achievements
    ? [
        {
          title: 'First Donation',
          description: 'Receive your first donation',
          value: uniqueGifts.length > 0,
          icon:
            uniqueGifts.length > 0
              ? FirstDonationUnlocked
              : FirstDonationLocked,
        },
        {
          title: 'Goal Met',
          description: 'Achieve 100% of your goal to unlock this badge',
          href: user?.personalPageUrl,
          value: achievements.goalMet,
          icon: achievements.goalMet ? GoalMetUnLocked : GoalMetLocked,
        },
        {
          title: 'High-5',
          description: 'Receive five separate donations',
          value: uniqueGifts.length > 4,
          icon: uniqueGifts.length > 4 ? High5Unlocked : High5Locked,
        },
        {
          title: 'Kickstart Donor',
          description: 'Kick off your fundraiser with a donation to yourself',
          href: user?.donationPage,
          value: achievements.kickstartDonor,
          icon: achievements.kickstartDonor
            ? KickstartUnLocked
            : KickstartLocked,
          actionTitle: 'Donate',
        },
        {
          title: 'Link to Facebook',
          description:
            'Create a Facebook fundraiser to share your story with the world',
          value: facebookFundraiserStatus?.active,
          icon: facebookFundraiserStatus?.active
            ? LinkToFacebookUnLocked
            : LinkToFacebookLocked,
          actionTitle: 'Create a Facebook Fundraiser',
          isLoading: linkFbIsLoading,
          onClick: handleFbLogin,
        },
        {
          title: 'Picture Perfect',
          description: 'Upload a photo to your fundraising page',
          href: user?.personalPageUrl,
          value: photoExists,
          icon: photoExists ? PicturePerfectUnLocked : PicturePerfectLocked,
          actionTitle: 'Upload a photo',
        },
        {
          title: 'Sharing is Caring',
          description:
            'Share your fundraiser on social media or copy a link to your fundraising page',
          href: user?.personalPageUrl,
          value: sharingIsCaringInteraction?.interaction.length,
          icon: sharingIsCaringInteraction?.interaction.length
            ? SharingIsCaringUnlocked
            : SharingIsCaringLocked,
          actionTitle: 'Share your fundraiser',
        },
        {
          title: 'Storyteller',
          description:
            'Tell the world your story by editing your personal page to unlock this badge',
          href: user?.personalPageUrl,
          value: achievements.storyTeller,
          icon: achievements.storyTeller
            ? StoryTellerUnLocked
            : StoryTellerLocked,
          actionTitle: 'Edit your story',
        },
        {
          title: 'Super Thankful',
          description: 'Send a thank you message to five separate donors',
          onClick: () => {
            navigateTo('/donations');
          },
          value: uniqueThankedGifts.length > 4,
          icon:
            uniqueThankedGifts.length > 4
              ? SuperThankfulUnlocked
              : SuperThankfulLocked,
          actionTitle: 'Send thank you messages',
        },
        {
          title: 'Thankful',
          description: 'Send a thank you message to a donor',
          onClick: () => {
            navigateTo('/donations');
          },
          value: uniqueThankedGifts.length > 0,
          icon:
            uniqueThankedGifts.length > 0 ? ThankfulUnlocked : ThankfulLocked,
          actionTitle: 'Send thank you messages',
        },
      ]
    : [];

  return {
    data: {
      facebookFundraiserId: achievements?.facebookFundraiserId,
      achievements: sortBy(['value', 'title'], CREATE_CLIENT_ACHIEVEMENTS),
      totalAchievements: CREATE_CLIENT_ACHIEVEMENTS.length,
      totalUnlockedAchievements: CREATE_CLIENT_ACHIEVEMENTS.filter(
        (a: any) => a.value
      ).length,
    },
    isInitialLoading,
    commands: {},
  };
};
